import { FC, ReactNode, RefObject, useEffect } from "react";

import Box from "@mui/material/Box";
import { createPortal } from "react-dom";
import { type Control, useWatch } from "react-hook-form";

import { QuickSearchProvider, useRateQuickSearch } from ".";

import { QuickAddSearch } from "@/dashboard-design-system/QuickAddSearch";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface RatesQuickNavProps {
  control: Control<StepTicketsFormData>;
  containerRef: RefObject<HTMLElement | null>;
  formSectionNode: HTMLElement;
  actionFooterHeightPx: number;
  gapSpacingUnits: number;
  isAddDisabled: boolean;
  addLabel: string;
  searchPlaceholder: string;
  counterLabel: (args: { current: number; total: number }) => string;
  onAdd: () => void;
  onCurrentMatchRateIndexChange: (index: number) => void;
  children: ReactNode;
}

export const RatesQuickNav: FC<RatesQuickNavProps> = ({
  control,
  containerRef,
  formSectionNode,
  actionFooterHeightPx,
  gapSpacingUnits,
  isAddDisabled,
  addLabel,
  searchPlaceholder,
  counterLabel,
  onAdd,
  onCurrentMatchRateIndexChange,
  children,
}) => {
  const rates = useWatch({ control, name: "rates" }) ?? [];
  const search = useRateQuickSearch({ rates, containerRef });

  useEffect(() => {
    onCurrentMatchRateIndexChange(search.currentMatchRateIndex);
  }, [search.currentMatchRateIndex, onCurrentMatchRateIndexChange]);

  return (
    <QuickSearchProvider query={search.query}>
      {children}
      {createPortal(
        <Box
          sx={(theme) => ({
            position: "absolute",
            bottom: `calc(${actionFooterHeightPx}px + ${theme.spacing(gapSpacingUnits)})`,
            right: theme.spacing(gapSpacingUnits),
            zIndex: 2,
          })}
        >
          <QuickAddSearch
            query={search.query}
            onQueryChange={search.setQuery}
            matchCount={search.matchCount}
            currentMatchIndex={search.currentMatchIndex}
            onPrevMatch={search.goToPrevMatch}
            onNextMatch={search.goToNextMatch}
            onAdd={onAdd}
            addLabel={addLabel}
            searchPlaceholder={searchPlaceholder}
            counterLabel={counterLabel}
            isAddDisabled={isAddDisabled}
            data-test="rate-quick-add-search"
          />
        </Box>,
        formSectionNode
      )}
    </QuickSearchProvider>
  );
};
